import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const Lightbox = ({ open, images, index, setIndex, onClose, alt }) => {
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + images.length) % images.length),
    [images.length, setIndex]
  );
  const next = useCallback(
    () => setIndex((i) => (i + 1) % images.length),
    [images.length, setIndex]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, prev, next]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-md flex items-center justify-center"
          onClick={onClose}
          data-testid="lightbox-overlay"
        >
          <button
            onClick={onClose}
            className="absolute top-5 right-5 z-20 p-2 text-white/60 hover:text-[var(--al-gold)] transition-colors duration-300"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X className="w-7 h-7" />
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 md:left-8 z-20 p-2 text-white/60 hover:text-[var(--al-gold)] transition-colors duration-300"
                aria-label="Previous"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-9 h-9" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 md:right-8 z-20 p-2 text-white/60 hover:text-[var(--al-gold)] transition-colors duration-300"
                aria-label="Next"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-9 h-9" />
              </button>
            </>
          )}

          <motion.img
            key={index}
            src={images[index]}
            alt={alt}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-[92vw] max-h-[86vh] object-contain select-none"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          {images.length > 1 && (
            <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                  className={`h-1.5 transition-all duration-400 ${
                    i === index ? "w-7 bg-[var(--al-gold)]" : "w-2 bg-white/30 hover:bg-white/60"
                  }`}
                  aria-label={`Image ${i + 1}`}
                  data-testid={`lightbox-dot-${i}`}
                />
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
